/**
 * @fileoverview App Layout Component
 *
 * Mobile-first application layout with:
 * - Toggle-only sidebar (overlay mode)
 * - Header with sidebar toggle, title, notifications, user menu
 * - Footer navigation bar (mobile only)
 * - Responsive design
 * - Uses Outlet for nested routes
 *
 * @module layout/app/AppLayout
 */

import React from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider } from '@xanectech/ui/components';
import { useIsMobile } from '@xanectech/ui/lib';
import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { AppFooterNav } from './AppFooterNav';
import { AppLayoutProps, MenuItem, FooterNavItem } from './app-config';
import { Home, FileText, MessageSquare, QrCode, Settings } from 'lucide-react';
import { useAccessibleProducts } from '@xanectech/data';
import { AppShell } from '../AppShell';

/**
 * Default menu items for sidebar
 */
const defaultMenuItems: MenuItem[] = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: Home,
  },
  {
    title: 'Data',
    url: '/data',
    icon: FileText,
  },
  {
    title: 'Messages',
    url: '/messages',
    icon: MessageSquare,
  },
  {
    title: 'Scan QR',
    url: '/scan',
    icon: QrCode,
  },
  {
    title: 'Settings',
    url: '/settings',
    icon: Settings,
  },
];

/**
 * Default footer navigation items
 */
const defaultFooterItems: FooterNavItem[] = [
  {
    id: 'dashboard',
    label: 'Home',
    icon: Home,
    url: '/dashboard',
  },
  {
    id: 'data',
    label: 'Data',
    icon: FileText,
    url: '/data',
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: MessageSquare,
    url: '/messages',
  },
  {
    id: 'scan',
    label: 'Scan',
    icon: QrCode,
    url: '/scan',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    url: '/settings',
  },
];

/**
 * AppLayout Component
 *
 * Main layout wrapper for user applications with mobile-first design.
 * Provides consistent navigation, header, and footer across the app.
 *
 * Supports both configuration-driven API and legacy prop-based API.
 *
 * @component
 
 */
export const AppLayout: React.FC<AppLayoutProps> = ({
  config,
  children,
  title = 'App',
  showFooterNav,
  sidebarTitle = 'Application',
  sidebarSubtitle = 'Navigation',
  appHeaderComponent,
}) => {
  const isMobile = useIsMobile();
  const products = useAccessibleProducts();

  // Use config-based API if config is provided
  if (config) {
    // Convert navigation items to footer items if not explicitly provided
    const footerItems: FooterNavItem[] =
      config.mobileFooterNav ??
      config.navigation
        .flatMap((group) =>
          group.items.slice(0, 5).map((item, idx) => ({
            id: item.href.replace(/\//g, '-') || `item-${idx}`,
            label: item.title,
            icon: item.icon,
            url: item.href,
            badge: item.badge,
            shownWhenLocked: item.shownWhenLocked,
          })),
        )
        .slice(0, 5);

    // Determine if footer nav should be shown
    const shouldShowFooter =
      config.showFooterNav !== undefined ? config.showFooterNav : isMobile;

    // Convert NavGroups to MenuItem[] for sidebar
    const menuItems: MenuItem[] = config.navigation.flatMap((group) =>
      group.items.map((item) => ({
        title: item.title,
        url: item.href,
        icon: item.icon,
        badge: item.badge,
        shownWhenLocked: item.shownWhenLocked,
      })),
    );

    return (
      <AppShell>
        <SidebarProvider>
          <div className='flex h-screen w-full bg-background overflow-hidden'>
            {/* Sidebar - always overlay mode */}
            <AppSidebar
              headerTitle={config.branding.name}
              headerSubtitle={config.user?.name || ''}
              groupLabel={config.navigation[0]?.label || 'Navigation'}
              menuItems={menuItems}
              logoSrc={config.branding.logo}
              products={products}
            />

            {/* Main content area */}
            <div className='flex flex-1 flex-col overflow-hidden'>
              {/* Header */}
              <AppHeader
                title={
                  config.navigation
                    .find((group) =>
                      group.items.some(
                        (item) => item.href === window.location.pathname,
                      ),
                    )
                    ?.items.find(
                      (item) => item.href === window.location.pathname,
                    )?.title || title
                }
                showSidebarToggle={true}
                showNotifications={true}
                showUserMenu={true}
                notifications={config.notifications}
                onLogout={config.onLogout}
                appHeaderComponent={appHeaderComponent}
                icon={
                  config.navigation
                    .find((group) =>
                      group.items.some(
                        (item) => item.href === window.location.pathname,
                      ),
                    )
                    ?.items.find(
                      (item) => item.href === window.location.pathname,
                    )?.icon
                }
              />

              {/* Main content with scroll */}
              <main
                className={`flex-1 overflow-y-auto bg-muted/10 ${shouldShowFooter ? 'pb-16' : ''}`}
              >
                <Outlet />
              </main>

              {/* Footer navigation - mobile only by default */}
              {shouldShowFooter && <AppFooterNav items={footerItems} />}
            </div>
          </div>
        </SidebarProvider>
      </AppShell>
    );
  }

  // Legacy prop-based API
  // Determine if footer nav should be shown
  const shouldShowFooter =
    showFooterNav !== undefined ? showFooterNav : isMobile;

  return (
    <AppShell>
      <SidebarProvider>
        <div className='flex h-screen w-full bg-background overflow-hidden'>
          {/* Sidebar - always overlay mode */}
          <AppSidebar
            headerTitle={sidebarTitle}
            headerSubtitle={sidebarSubtitle}
            groupLabel='Navigation'
            menuItems={defaultMenuItems}
          />

          {/* Main content area */}
          <div className='flex flex-1 flex-col overflow-hidden'>
            {/* Header */}
            <AppHeader
              title={title}
              showSidebarToggle={true}
              showNotifications={true}
              showUserMenu={true}
            />

            {/* Main content with scroll */}
            <main
              className={`flex-1 overflow-y-auto bg-muted/10 ${shouldShowFooter ? 'pb-16' : ''}`}
            >
              {children || <Outlet />}
            </main>

            {/* Footer navigation - mobile only by default */}
            {shouldShowFooter && <AppFooterNav items={defaultFooterItems} />}
          </div>
        </div>
      </SidebarProvider>
    </AppShell>
  );
};
